/* eslint-disable no-param-reassign */
import {
  useCreateCompanyBankDetails,
  useCurrentCompanyBankDetails,
  useSetRepairersActivity,
  useUpdateCompanyBankDetails,
} from 'app/core/api/company/api-company';
import {
  CompanyBankDetails,
  PageDetails,
} from 'app/core/api/company/api-company.types';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import {
  useEffectOnce,
  useEffectOnChanges,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

export default function UpdateBankDetails(): JSX.Element {
  useSetPageTitle('Business Bank Details');
  const history = useHistory();

  const { request$: repairActivity } = useSetRepairersActivity();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Business Bank Details',
    };
    repairActivity(pageDetails).subscribe();
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    request$: updateCompany$,
    error: updateError,
    sending: updatingCompany,
  } = useUpdateCompanyBankDetails();

  const {
    request$: createCompany$,
    error: createError,
    sending: creatingCompany,
  } = useCreateCompanyBankDetails();

  const {
    resource: company,
    loading,
    error,
  } = useCurrentCompanyBankDetails({
    fresh: true,
  });
  const { showSnack } = useShowInfo();

  const editable$ = useMemo(() => new BehaviorSubject(false), []);
  const editable = useObservableEagerState(editable$);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, inputProps, formState } =
    useInputForm<CompanyBankDetails>({
      mode: 'onTouched',
      defaultValues: company,
      apiError: updateError,
    });

  // Click cancel button -> get navigated back automatically
  useEffectOnChanges(() => {
    if (
      !editable &&
      !updatingCompany &&
      !updateError &&
      !createError
    ) {
      history.goBack();
    }
  }, [editable, history, updateError, createError, updatingCompany]);
  useEffect(() => {
    if (error) {
      editable$.next(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  // Click cancel button -> get navigated back automatically
  return (
    <>
      <Form
        flexStretch
        onSubmit={handleSubmit(details => {
          if (_.isEmpty(formState.touched)) {
            history.goBack();
            return;
          }
          details.accountholdertype = 'individual';
          details.currency = 'gbp';
          if (company) {
            return updateCompany$(details).pipe(
              tap(() => {
                showSnack({
                  message: 'Updated successfully!',
                  alertType: 'info',
                });
                history.goBack();
              })
            );
          } else {
            return createCompany$(details).pipe(
              tap(() => {
                showSnack({
                  message: 'Created successfully!',
                  alertType: 'info',
                });
                history.goBack();
              })
            );
          }
        })}
      >
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Business Bank Details
        </Body>
        <FormTextInput
          controlName="accountholdername"
          placeholder="Account Holder Name"
          autoComplete="accountholdername"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="sortcode"
          placeholder="Sort Code"
          autoComplete="sortcode"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="accountnumber"
          autoComplete="accountnumber"
          placeholder="Account Number"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="confirmaccountnumber"
          autoComplete="confirmaccountnumber"
          placeholder="Confirm Account Number"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="bankname"
          placeholder="Bank Name"
          disabled={!editable}
          {...inputProps}
        />
        <Body tiny>Asterisks (*) indicate required fields</Body>
        <ErrorText
          message={
            (updateError && updateError.errorMessage) ??
            (createError && createError.errorMessage)
          }
        />

        <BottomEditActions
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          sending={updatingCompany ?? creatingCompany}
          {...{ editable$, loading }}
        />
      </Form>
    </>
  );
}
